import { useCallback, useRef, useState } from "react";

/**
 * Measures the live pixel height of a DOM node and keeps it in sync as the node
 * resizes (via ResizeObserver). Used to size content padding to overlaid
 * header/footer bands whose height is data-driven (title length, safe-area
 * insets, keyboard state) and not known ahead of render.
 *
 * Exposes a *callback ref* rather than an effect keyed to a RefObject: the
 * measured node often mounts after the first render (e.g. behind a loading
 * gate), and a callback ref measures exactly when the node attaches — an
 * empty-dependency effect would miss that later mount.
 */
export function useMeasuredHeight() {
  const [height, setHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    const existing = observerRef.current;
    if (existing !== null) {
      existing.disconnect();
      observerRef.current = null;
    }
    if (node === null) return;

    const update = (): void => {
      setHeight(node.offsetHeight);
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return { ref, height };
}
